import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { getPaymentDetailOptions } from '@/lib/client/@tanstack/react-query.gen'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

export const Route = createFileRoute('/link/$parentId/payments/$paymentId')({
  component: PaymentDetailPage,
})

function PaymentDetailPage() {
  const { parentId, paymentId } = Route.useParams()

  const { data: paymentDetail, isLoading } = useQuery({
    ...getPaymentDetailOptions({
      path: { parentId, paymentId },
    }),
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    )
  }

  if (!paymentDetail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-red-600">Paiement introuvable</div>
      </div>
    )
  }

  const payment = paymentDetail.payment
  const invoiceAllocations = paymentDetail.invoiceAllocations

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return <Badge className="bg-green-500">Réussi</Badge>
      case 'PENDING':
        return <Badge className="bg-orange-500">En attente</Badge>
      case 'FAILED':
        return <Badge className="bg-red-500">Échoué</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CARD':
        return 'Carte bancaire'
      case 'SEPA':
        return 'Prélèvement SEPA'
      case 'BANK_TRANSFER':
        return 'Virement bancaire'
      case 'MANUAL':
        return 'Manuel'
      default:
        return method
    }
  }

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'STRIPE':
        return 'Stripe'
      case 'HELLOASSO':
        return 'HelloAsso'
      case 'INTERNAL_DEBT_FORGIVENESS':
        return 'Effacement de dette'
      default:
        return provider
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/link/$parentId/payments" params={{ parentId }}>
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour aux paiements
            </Button>
          </Link>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">Détail du paiement</h1>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(payment.status)}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations du paiement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {format(new Date(payment.createdAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Montant</p>
                  <p className="text-lg font-bold">{payment.amount.toFixed(2)} €</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Méthode de paiement</p>
                  <p className="font-medium">{getPaymentMethodLabel(payment.paymentMethod)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fournisseur</p>
                  <p className="font-medium">{getProviderLabel(payment.paymentProvider)}</p>
                </div>
              </div>

              {payment.failureMessage && (
                <>
                  <Separator />
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="ml-2">
                      <strong>Raison de l'échec :</strong> {payment.failureMessage}
                    </AlertDescription>
                  </Alert>
                </>
              )}

              {payment.comment && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Commentaire</p>
                    <p className="text-sm bg-gray-50 p-3 rounded">{payment.comment}</p>
                  </div>
                </>
              )}

              {payment.refunded && (
                <>
                  <Separator />
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="ml-2">
                      <div className="space-y-1">
                        <p>
                          <strong>Paiement remboursé</strong>
                        </p>
                        <p className="text-sm">
                          Montant remboursé : <span className="font-medium">{payment.refundAmount?.toFixed(2)} €</span>
                        </p>
                        {payment.refundDate && (
                          <p className="text-sm">
                            Date : {format(new Date(payment.refundDate), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                          </p>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>

          {invoiceAllocations && invoiceAllocations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Répartition sur les factures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-sm font-medium text-gray-500">Facture</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-500">Montant alloué</th>
                        <th className="text-right py-2 text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceAllocations.map((allocation) => (
                        <tr key={allocation.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 text-sm font-medium">
                            {allocation.invoiceNumber}
                          </td>
                          <td className="py-3 text-sm text-right font-medium">
                            {allocation.allocatedAmount.toFixed(2)} €
                          </td>
                          <td className="py-3 text-right">
                            <Link
                              to="/link/$parentId/invoices/$invoiceId"
                              params={{ parentId, invoiceId: allocation.invoiceId }}
                            >
                              <Button variant="link" size="sm" className="p-0 h-auto">
                                Voir la facture
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-500">Total</span>
                    <span className="text-lg font-bold">
                      {invoiceAllocations
                        .reduce((sum, allocation) => sum + allocation.allocatedAmount, 0)
                        .toFixed(2)}{' '}
                      €
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
